import type React from 'react';
import type { ComponentType, SVGProps } from 'react';
import {
  SegmentationControllerType,
  SegmentationProps,
  getPlaqueDisplayType,
} from 'config/analysisConfig';
import type { CPRBufferKeys } from '../../rendering/Panels/Renderers/vtkManagers';
import { SEGMENTATION_CONTROLLER_NAMES } from 'config/labels';
import styles from '../SegmentManagerList.module.scss';
import { configStore } from 'config/configStore';
import type { MouseTooltipHandlers } from '@components/Tooltip/useMouseTooltip';

type SvgIcon = ComponentType<SVGProps<SVGSVGElement>>;

type ManualViewTarget = '2D' | '3D';
type ManualColors = Record<ManualViewTarget, [number, number, number]>;

type Props = {
  sorted: [string, SegmentationProps][];
  pool?: Partial<Record<CPRBufferKeys, { data?: unknown }>>;
  selectedKey: SegmentationControllerType;
  manualColors: ManualColors;
  manualMode: boolean;
  hasVR: boolean;
  segmentationReady: boolean;
  coronaryReady: boolean;
  isSegmentedRegionSelected: boolean;
  isManual3DVisible: boolean;
  mipEnabled?: boolean;
  ShowIcon: SvgIcon;
  HideIcon: SvgIcon;
  onSelectKey: (key: SegmentationControllerType) => void;
  onToggleShow: (key: SegmentationControllerType) => void;
  onColorMenuToggle: (key: SegmentationControllerType) => void;
  onManualColorChange: (
    target: ManualViewTarget,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onManualToggle: (target: ManualViewTarget) => void;
  tooltipHandlers: MouseTooltipHandlers;
};

export default function SegmentationVisibilityList({
  sorted,
  pool,
  selectedKey,
  manualColors,
  manualMode,
  hasVR,
  segmentationReady,
  coronaryReady,
  isSegmentedRegionSelected,
  isManual3DVisible,
  mipEnabled,
  ShowIcon,
  HideIcon,
  onSelectKey,
  onToggleShow,
  onColorMenuToggle,
  onManualColorChange,
  onManualToggle,
  tooltipHandlers,
}: Props) {
  const renderItem = (key: string, val: SegmentationProps) => {
    const targetKey = key as SegmentationControllerType;
    const keyName = `segmentationResult_${key}` as CPRBufferKeys;
    const hasSegmentData = !!pool?.[keyName]?.data;
    const isCoronaryOnly = key === 'coronary' || key === 'calcium';
    const isPlaqueSegment = getPlaqueDisplayType(key);
    const isAorta = key === 'aorta';
    const isSegmentDisabled = !hasSegmentData
      ? true
      : isPlaqueSegment && hasVR
        ? true
        : isCoronaryOnly
          ? !coronaryReady
          : isAorta
            ? !(coronaryReady || segmentationReady)
            : !segmentationReady;

    const visible =
      !hasSegmentData || (isPlaqueSegment && hasVR)
        ? false
        : val.visibility && val.opacity !== 0;

    const disabled = !!mipEnabled || isSegmentDisabled;
    const segmentName = SEGMENTATION_CONTROLLER_NAMES[targetKey];

    return (
      <div
        className={`${styles.tableItem} ${key === selectedKey ? styles.selected : ''} ${disabled ? styles.disabled : ''}`}
        onClick={() => onSelectKey(targetKey)}
      >
        <div className={styles.colorText}>
          <div
            className={styles.colorBox}
            onClick={() => {
              if (manualMode) return;
              onColorMenuToggle(targetKey);
            }}
            style={{
              backgroundColor: `rgb(${val.color[0]}, ${val.color[1]}, ${val.color[2]})`,
            }}
          />
          <span
            className={styles.textContainer}
            data-name={segmentName}
            onMouseEnter={tooltipHandlers.onMouseEnter}
            onMouseMove={tooltipHandlers.onMouseMove}
            onMouseLeave={tooltipHandlers.onMouseLeave}
          >
            {segmentName}
          </span>
        </div>
        <div
          className={styles.eyeContainer}
          onClick={() => {
            onToggleShow(targetKey);
            configStore.setData({ laText: false});
          }}
        >
          {manualMode ? (
            key === selectedKey ? (
              <ShowIcon />
            ) : (
              <HideIcon />
            )
          ) : visible ? (
            <ShowIcon />
          ) : (
            <HideIcon />
          )}
        </div>
      </div>
    );
  };

  const renderManualColor = (
    manualColor: [number, number, number],
    onColorChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  ) => (
    <div
      className={styles.colorPickerWrapper}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={styles.colorBox}
        style={{
          backgroundColor: `rgb(${manualColor[0]}, ${manualColor[1]}, ${manualColor[2]})`,
        }}
      />
      <input
        type="color"
        className={styles.manualColorInput}
        value={`#${manualColor
          .map((v) => v.toString(16).padStart(2, '0'))
          .join('')}`}
        onChange={onColorChange}
      />
    </div>
  );

  const renderManualItem = (
    key: string,
    label: string,
    manualColor: [number, number, number],
    onColorChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
    visible: boolean,
    onClick: () => void,
  ) => {
    return (
      <div
        key={key}
        className={`${styles.tableItem}`}
        onClick={onClick}
      >
        <div className={styles.colorText}>
          {renderManualColor(manualColor, onColorChange)}
          <span
            className={styles.textContainer}
            data-name={label}
            onMouseEnter={tooltipHandlers.onMouseEnter}
            onMouseMove={tooltipHandlers.onMouseMove}
            onMouseLeave={tooltipHandlers.onMouseLeave}
          >
            {label}
          </span>
        </div>

        <div className={styles.eyeContainer}>
          {visible ? <ShowIcon /> : <HideIcon />}
        </div>
      </div>
    );
  };

  const items = manualMode
  ? [
      renderManualItem(
        'manual-3d',
        '3D View',
        manualColors['3D'],
        (event) => onManualColorChange('3D', event),
        isManual3DVisible,
        () => onManualToggle('3D'),
      ),
      renderManualItem(
        'manual-2d',
        'CT View',
        manualColors['2D'],
        (event) => onManualColorChange('2D', event),
        isSegmentedRegionSelected,
        () => onManualToggle('2D'),
      ),
    ]
  : sorted.map(([key, val]) => renderItem(key, val));

  return (
    <>
      {items.map((left, index, arr) => {
        if (index % 2 !== 0) return null;
        const right =
          index + 1 < arr.length ? (
            arr[index + 1]
          ) : (
            <div key="empty" className={`${styles.tableItem} ${styles.empty}`} />
          );
        return (
          <div key={index} className={styles.container}>
            {left}
            {right}
          </div>
        );
      })}
    </>
  );
}
